/**
 * useAuth — Stalkie Web
 *
 * Delegates to AuthContext, which is backed by @convex-dev/auth.
 * Keeps the same API surface for existing pages.
 */
import { useAuthContext } from "../auth/AuthContext";
import type { EntitlementState, UserSession } from "../domain/types";

export function useAuth() {
  const { isLoading, isAuthenticated, user, signOut, createAnonymousSession } =
    useAuthContext();

  // Build a UserSession-compatible shape from the Convex user record
  const session: UserSession | null = user
    ? {
        userId: user._id,
        isAnonymous: !user.email && !user.appleUserId,
        createdAt: new Date(user.createdAt).toISOString(),
      }
    : null;

  // Build entitlement from user record
  const entitlement: EntitlementState | null = user
    ? {
        isPremium: user.isPremium ?? false,
        planKey: user.planKey ?? "free",
        planLimit: user.planLimit ?? 0,
      }
    : null;

  return {
    session,
    entitlement,
    isLoading,
    isAuthenticated,
    signOut: async () => {
      await signOut();
    },
    createAnonymousSession,
    // Legacy no-op
    refreshEntitlement: async () => {},
  };
}
